import {Head} from '@aboutyou/head-elements';
import {useTranslation} from '@aboutyou/i18n';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {useSearch} from '@aboutyou/router';
import {UnstyledLink} from '@aboutyou/ui-components';
import React, {FC} from 'react';
import styled from 'styled-components';
import {
  PathIntent,
  getAppIntentUrlForDevices,
} from '../../../features/AppRedirect/helpers';
import {useGetOSPRedirectValue} from '../../../shared-deprecated/useGetOSPRedirectValue';

const RedirectContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 30vh;
  padding-top: 15vh;
  justify-content: space-evenly;
  gap: 20px;
`;

const RedirectMessage = styled.div`
  margin: 0 32px;
  font-size: 16px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: normal;
  text-align: center;
  color: #434343; // inlining color to not increase bundle size by ~ 30kb
`;

const RedirectButton = styled(UnstyledLink)`
  padding: 16px;
  margin: 0 32px;
  text-align: center;
  border-radius: 2px;
  color: white;
  background-color: black;
  font-size: 14px;
  font-weight: bold;
  align-self: stretch;
  &:hover,
  &:visited {
    color: white;
  }
`;

const RedirectNotice = styled.div`
  padding: 0 32px;
  margin-bottom: 72px; /* additional margin-bottom to avoid an overlapping android navigation bar */
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  color: #9d9d9d; // inlining color to not increase bundle size by ~ 30kb
`;
/**
 * TLDR: This component returns the user to the native OSP.
 *
 * The Web Checkout page is embedded and used by the Mobile App to complete the purchase.
 * However the new Checkout V3 integration does not differentiate between web and mobile
 * and always redirects the user to /order/success once the order is complete.
 *
 * Instead of fetching the transaction data and showing it to the user we need to trigger
 * a 'refresh' which will redirect the user back to the Mobile App.
 *
 * Each platform - Android, iOS needs a specific URL to open the native app. The decision which
 * one should be used is based on the 'device' search parameter use on /checkout
 * route eg `/checkout?device=ios|android`
 *
 * Example:
 * iOS: appaboutyou:/checkout/success?cbd=...&pmm=...
 *
 * Note: Always use .de domain
 * Android: intent://m.aboutyou.de/checkout/success?cbd=...&pmm=...#Intent;package=de.aboutyou.mobile.app;scheme=https;end
 *
 */
export const ForceAppRedirect: FC<
  React.PropsWithChildren<{intent: PathIntent}>
> = ({intent}) => {
  const search = useSearch();
  const isHydrationDone = useHydrationDone();
  const device = useGetOSPRedirectValue();
  const {t} = useTranslation();

  const url = getAppIntentUrlForDevices({
    device: device!,
    intent,
    searchParams: search,
  });

  if (!url || !isHydrationDone) {
    return null;
  }

  return (
    <>
      <Head>
        <meta httpEquiv="refresh" content={`0; url=${url}`} />
      </Head>
      <RedirectContainer>
        <RedirectMessage>
          {t('Just a moment, we are redirecting you to the App')}
        </RedirectMessage>
        <RedirectButton to={url}>{t('Back to the App')}</RedirectButton>
        <RedirectNotice>
          {t('If you are not redirected automatically, press this button')}
        </RedirectNotice>
      </RedirectContainer>
    </>
  );
};
